<template>
  <div class="dictionary-page">
    <div class="toolbar card-surface">
      <div class="left">
        <el-button type="primary" icon="el-icon-plus" @click="openAddDialog('room', null)">新增空间类型</el-button>
        <el-select v-model="filterStatus" placeholder="按状态筛选" clearable size="small" style="margin-left:12px; width:140px;">
          <el-option v-for="opt in filterOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>
      </div>
      <div class="right">
        <!-- Excel 导出/导入 -->
        <el-button size="small" icon="el-icon-download" v-admin @click="handleExportExcel">导出Excel</el-button>
        <el-button size="small" icon="el-icon-document" v-admin @click="handleDownloadTemplate">下载模板</el-button>
        <el-upload
          ref="excelUpload"
          class="excel-uploader"
          :action="'/'"
          :show-file-list="false"
          :auto-upload="false"
          accept=".xlsx,.xls"
          :on-change="handleImportChange"
          :disabled="importing"
        >
          <el-button :loading="importing" v-admin size="small" icon="el-icon-upload2">导入Excel</el-button>
        </el-upload>

        <el-button icon="el-icon-refresh" @click="fetchTree">刷新</el-button>
      </div>
    </div>

    <div class="content-grid">
      <div class="card-surface pane-left">
        <div class="pane-block">
          <div class="pane-block__header">
            <div class="pane-block__title">空间类型（一级）</div>
          </div>
          <common-table
            class="dict-select-table"
            :columns="roomTableColumns"
            :data="treeData"
            :loading="loading"
            :show-pagination="false"
            row-key="id"
            :row-class-name="roomRowClassName"
            empty-text="暂无空间类型"
            @row-click="handleRoomRowClick"
          >
            <template #col-status="{ row }">
              <el-tag :type="row && row.status === 'enabled' ? 'success' : 'info'">
                {{ row && row.status === 'enabled' ? '开启' : '关闭' }}
              </el-tag>
            </template>
            <template #op-actions="{ row }">
              <el-button type="text" size="mini" @click.stop="openAddDialog(row.level, row)">新增子级</el-button>
              <el-button type="text" size="mini" @click.stop="openEditDialog(row)">编辑</el-button>
              <el-button type="text" size="mini" v-admin style="color: #f56c6c;" @click.stop="handleDelete(row)">删除</el-button>
            </template>
          </common-table>
        </div>

        <div class="pane-block">
          <div class="pane-block__header">
            <div class="pane-block__title">家电类别（二级）</div>
            <div class="pane-block__meta">当前空间：{{ selectedRoomRow ? selectedRoomRow.name : '未选择' }}</div>
          </div>
          <common-table
            class="dict-select-table"
            :columns="applianceTableColumns"
            :data="applianceList"
            :loading="appliancesLoading"
            :show-pagination="false"
            row-key="id"
            :row-class-name="applianceRowClassName"
            :empty-text="selectedRoomRow ? '暂无家电类别' : '请先选择空间类型'"
            @row-click="handleApplianceRowClick"
          >
            <template #col-status="{ row }">
              <el-tag :type="row && row.status === 'enabled' ? 'success' : 'info'">
                {{ row && row.status === 'enabled' ? '开启' : '关闭' }}
              </el-tag>
            </template>
            <template #op-actions="{ row }">
              <el-button type="text" size="mini" v-if="row && row.level !== 'product' && row.level !== 'entrance'" @click.stop="openAddDialog(row.level, row)">新增子级</el-button>
              <el-button type="text" size="mini" @click.stop="openEditDialog(row)">编辑</el-button>
              <el-button type="text" size="mini" v-admin style="color: #f56c6c;" @click.stop="handleDelete(row)">删除</el-button>
            </template>
          </common-table>
        </div>
      </div>

      <div class="card-surface pane-right">
        <div class="pane-block__header pane-block__header--right">
          <div class="pane-block__title">产品（三级）</div>
          <div class="pane-block__meta">
            当前路径：
            {{ selectedRoomRow ? selectedRoomRow.name : '-' }}
            / {{ selectedApplianceRow ? selectedApplianceRow.name : '未选择家电类别' }}
          </div>
          <div class="pane-block__actions">
            <el-button
              type="primary"
              size="small"
              icon="el-icon-plus"
              :disabled="!selectedApplianceRow"
              @click="openAddDialog('product', selectedApplianceRow)"
            >新增产品</el-button>
          </div>
        </div>
        <common-table
          class="dict-product-table"
          :columns="productTableColumns"
          :data="productList"
          :loading="productsLoading"
          :max-height="productTableMaxHeight"
          :show-pagination="Boolean(selectedApplianceRow)"
          :pagination="productPagination"
          row-key="id"
          empty-text="请选择家电类别后查看产品"
          @page-change="handleProductPageChange"
        >
          <template #col-platform="{ row }">
            {{ (platformMap[row.platform] ? platformMap[row.platform] :  '-') }}
          </template>
          <template #col-picture="{ row }">
            <el-image
              v-if="resolvePictureUrl(row.picture)"
              :src="resolvePictureUrl(row.picture)"
              :preview-src-list="picturePreviewList(row.picture)"
              fit="cover"
              style="width: 42px; height: 42px; border-radius: 4px;"
            >
              <div slot="error" class="dict-img__error">-</div>
            </el-image>
            <span v-else>-</span>
          </template>
          <template #col-level="{ row }">
            {{ (levelMap && row && row.level && levelMap[row.level]) ? levelMap[row.level] : (row && row.level ? row.level : '-') }}
          </template>
          <template #col-status="{ row }">
            <el-tag :type="row && row.status === 'enabled' ? 'success' : 'info'">
              {{ row && row.status === 'enabled' ? '开启' : '关闭' }}
            </el-tag>
          </template>
          <template #op-actions="{ row }">
            <el-button type="text" size="mini" v-if="row && row.level !== 'product' && row.level !== 'entrance'" @click="openAddDialog(row.level, row)">新增子级</el-button>
            <el-button type="text" size="mini" @click="openEditDialog(row)">编辑</el-button>
            <el-button type="text" size="mini" v-admin style="color: #f56c6c;" @click="handleDelete(row)">删除</el-button>
          </template>
        </common-table>
      </div>
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="50%">
      <el-form ref="form" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入名称" />
        </el-form-item>
        <el-form-item label="层级" prop="level">
          <el-select v-model="form.level" placeholder="请选择层级">
            <el-option
                v-for="item in levelOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item v-if="form.level === 'entrance'" label="入口地址" prop="entranceUrl">
          <el-input v-model="form.entranceUrl" placeholder="请输入口地址" />
        </el-form-item>

        <!-- 仅产品层级展示的“产品信息”字段 -->
        <el-form-item v-if="isProductLevel" label="分数">
          <el-input-number v-model="form.score" :min="0" :max="100"/>
        </el-form-item>
        <el-form-item v-if="isProductLevel" label="适用平台">
          <el-select
            v-model="form.platform"
            placeholder="请选择适用平台"
            clearable
            filterable
          >
            <el-option
              v-for="opt in platformOptions"
              :key="String(opt.value)"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="状态" prop="status">
          <el-select v-model="form.status" placeholder="请选择状态">
            <el-option
                v-for="item in statusOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="图标" prop="icon" v-if="form.level === 'room' || form.level === 'entrance'">
          <el-upload
              :class="{'is-disabled': (form.icon || iconUrl)}"
              :action="`${baseUrl}/uploads/image`"
              :headers="upheaders"
              class="icon-upload"
              :before-upload="beforeUpload"
              :show-file-list="false"
              :on-success="handleIconSuccess"
              accept="png,jpg,jpeg,gif,bmp,svg"
          >
            <i v-if="form.icon" class="el-icon-error" @click.stop.prevent="clearIconUrl" />
            <img v-if="form.icon" :src="form.icon" class="icon-preview" alt="图标">
            <i v-else class="el-icon-plus avatar-uploader-icon" />
          </el-upload>
        </el-form-item>
        <el-form-item label="背景颜色" prop="bg" v-if="form.level === 'room' || form.level === 'entrance'">
          <el-color-picker v-model="form.bg" size="small" />
        </el-form-item>
        <el-form-item label="图片" v-if="isProductLevel" >
          <el-upload
              :class="{'is-disabled': (form.picture || imageUrl)}"
              :action="`${baseUrl}/uploads/image`"
              :headers="upheaders"
              class="upload-demo"
              :before-upload="beforeUpload"
              :show-file-list="false"
              :on-success="handleAvatarSuccess"
              accept="png,jpg,jpeg,gif,bmp"
          >
            <i v-if="form.picture" class="el-icon-error" @click="clearImageUrl" />
            <img v-if="form.picture" :src="form.picture" class="avatar" alt="图片">
            <i v-else class="el-icon-plus avatar-uploader-icon" />
          </el-upload>
        </el-form-item>
        <el-form-item label="描述" v-if="isProductLevel">
          <el-input v-model="form.description" placeholder="请输入描述" />
        </el-form-item>

        <!-- 仅产品层级展示规格 -->
        <el-form-item v-if="isProductLevel" label="规格">
          <div class="spec-editor">
            <div class="spec-editor__toolbar">
              <el-button size="mini" type="primary" plain icon="el-icon-plus" @click="addSpecItem()">新增规格项</el-button>
            </div>

            <div v-if="!form.specItems || form.specItems.length === 0" class="spec-editor__empty">
              暂无规格项，可点击“新增规格项”添加
            </div>

            <div v-for="(item, idx) in form.specItems" :key="item._key" class="spec-editor__row">
              <el-select
                v-model="item.key"
                size="mini"
                filterable
                clearable
                placeholder="选择规格字段"
                class="spec-editor__key"
                @change="onSpecKeyChange(item)"
              >
                <el-option
                  v-for="opt in getSpecKeyOptionsForItem(item)"
                  :key="String(opt.value)"
                  :label="opt.label"
                  :value="opt.value"
                />
              </el-select>

              <el-select
                v-model="item.type"
                size="mini"
                class="spec-editor__type"
                placeholder="类型"
                :disabled="!item.key || Boolean(getSpecMeta(item.key)?.type)"
              >
                <el-option label="文本" value="string" />
                <el-option label="数字" value="number" />
                <el-option label="布尔" value="boolean" />
              </el-select>

              <el-input
                v-if="item.type === 'string'"
                v-model.trim="item.value"
                size="mini"
                placeholder="值"
                class="spec-editor__value"
                :disabled="!item.type"
              />
              <el-input-number
                v-else-if="item.type === 'number'"
                v-model="item.value"
                size="mini"
                :controls="false"
                class="spec-editor__value"
                :disabled="!item.type"
              />
              <el-select
                v-else
                v-model="item.value"
                size="mini"
                class="spec-editor__value"
                placeholder="布尔"
                :disabled="!item.type"
              >
                <el-option label="true" :value="true" />
                <el-option label="false" :value="false" />
              </el-select>

              <el-button
                type="text"
                size="mini"
                class="spec-editor__remove"
                @click="removeSpecItem(idx)"
              >
                删除
              </el-button>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getApplianceRooms, getApplianceBundle, getApplianceProducts, createDictionary, updateDictionary, deleteDictionary,
  exportDictionaryTreeExcel, exportDictionaryTreeExcelTemplate, importDictionaryTreeExcel, getSpecifications } from '@/api/dictionary'
import {getToken} from '@/utils/auth';
import CommonTable from '@/components/commonTable.vue'

export default {
  name: 'DictionaryManage',
  components: { CommonTable },
  data() {
    return {
      upheaders: {
        Authorization: `Bearer ${getToken()}`
      },
      loading: false,
      treeData: [],
      baseUrl: process.env.VUE_APP_BASE_API,
      basePreviewUrl: process.env.VUE_APP_BASE_PREVIEW_API,
      dialogVisible: false,
      dialogTitle: '新增节点',
      columns: [
        { type: 'expand', label: '', width: 48 },
        { prop: 'name', label: '名称', minWidth: 220, align: 'left' },
        { prop: 'level', label: '层级', minWidth: 100, align: 'center' },
        { prop: 'picture', label: '图片', minWidth: 90, align: 'center' },
        { prop: 'platform', label: '适用平台', minWidth: 120, align: 'center' },
        { prop: 'score', label: '分数', minWidth: 80, align: 'center' },
        { prop: 'status', label: '状态', minWidth: 100, align: 'center' },
        { type: 'operation', prop: 'actions', label: '操作', width: 220, align: 'right' }
      ],
      // 层级显示映射（模板里会用到）
      levelMap: {
        room: '空间类型',
        appliance: '家电',
        product: '产品',
        entrance: '入口类型'
      },
      form: {
        id: null,
        parent: null,
        name: '',
        // 默认层级给一个合法值，避免渲染/校验时出现 undefined
        level: 'room',
        order: 0,
        score: 0,
        status: 'enabled',
        icon: '',
        bg: '',
        picture: '',
        description: '',
        platform: '',
        entranceUrl: '',
        // specs 作为提交结构（对象）保留，但 UI 使用 specItems/specFeatures
        specs: {},
        specItems: [],
        specFeatures: []
      },
      rules: {
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        level: [{ required: true, message: '请选择层级', trigger: 'change' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      },
      imageUrl:'',
      iconUrl: '',
      levelOptions: [
        { label: '空间类型', value: 'room' },
        { label: '家电', value: 'appliance' },
        { label: '产品', value: 'product' },
        { label: '入口类型', value: 'entrance' }
      ],
      statusOptions: [
        { label: '开启', value: 'enabled' },
        { label: '关闭', value: 'disabled' }
      ],

      // 适用平台：先用可配置映射（你把“如图映射”发我后，我再按你的值精确替换）
      // 约定：form.platform 存 value；显示用 label。
      platformOptions: [
        { label: '抖音', value: 'douyin' },
        { label: '美团', value: 'meituan' },
        { label: '天猫', value: 'tmall' },
        { label: '京东', value: 'jd' }
      ],
      platformMap: {
        douyin: '抖音',
        meituan: '美团',
        tmall: '天猫',
        jd: '京东'
      },
      // status filter: '' means show all, otherwise 'enabled' or 'disabled'
      filterStatus: 'all',
      filterOptions: [
        { label: '全部', value: 'all' },
        { label: '启用', value: 'enabled' },
        { label: '禁用', value: 'disabled' }
      ],
      importing: false,

      viewportHeight: (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 800,

      // 两栏联动展示用的选中状态
      selectedRoomId: null,
      selectedApplianceId: null,
      selectedRoomRow: null,
      selectedApplianceRow: null,
      applianceList: [],
      productList: [],
      productPagination: {
        page: 1,
        pageSize: 10,
        total: 0,
        pageSizes: [10, 20, 50, 100]
      },

      // 规格元数据（接口返回）
      specsMetaLoading: false,
      specsMetaMap: {}, // key -> { label, type?, ... }
      specKeyOptions: [], // [{label,value,type?}]
      // 每行的子节点加载状态（按需填充到行对象上，例如 row._loadedChildren / row._loadingChildren / row._pagination ）
    }
  },
  computed: {
     isProductLevel() {
       return this.form && this.form.level === 'product'
     },
     roomTableColumns() {
       return [
         { prop: 'name', label: '名称', minWidth: 160, align: 'left' },
         { prop: 'status', label: '状态', width: 90, align: 'center' },
         { type: 'operation', prop: 'actions', label: '操作', width: 180, align: 'right' }
       ]
     },
     applianceTableColumns() {
       return [
         { prop: 'name', label: '名称', minWidth: 160, align: 'left' },
         { prop: 'status', label: '状态', width: 90, align: 'center' },
         { type: 'operation', prop: 'actions', label: '操作', width: 200, align: 'right' }
       ]
     },
     productTableColumns() {
       return (this.columns || []).filter(col => col.type !== 'expand').map(col => {
         if (col && col.type === 'operation') {
           const { fixed: _fixed, ...rest } = col
           return { ...rest, fixed: undefined, align: 'right' }
         }
         return col
       })
     },
     appliancesLoading() {
       return Boolean(this.selectedRoomRow && this.selectedRoomRow._loadingChildren)
     },
     productsLoading() {
       return Boolean(this.selectedApplianceRow && this.selectedApplianceRow._loadingChildren)
     },
     productTableMaxHeight() {
       // 让滚动发生在表格内部，避免整页横向/纵向被表格撑出滚动条
       // 这里留出顶部 toolbar + 卡片间距 + header/pagination 的空间
       const h = Number(this.viewportHeight) || 800
       return Math.max(h - 320, 260)
     }
  },
  watch: {
    filterStatus() {
      // Backend filtering: refetch tree with status query param
      this.fetchTree()
    },
    'form.level'(newLevel, oldLevel) {
      // 只要从产品层级切走（例如 product -> room/appliance），就清空产品专属字段
      if (oldLevel === 'product' && newLevel !== 'product') {
        this.form.score = 0
        this.form.platform = ''
        this.form.specs = {}
        this.form.specItems = []
        this.form.specFeatures = []

        // 清理“规格”相关的校验/错误提示（否则可能残留红框）
        this.$nextTick(() => {
          if (this.$refs.form && this.$refs.form.clearValidate) {
            this.$refs.form.clearValidate()
          }
        })
      }
      // 离开 entrance 层级时清理入口地址
      if (oldLevel === 'entrance' && newLevel !== 'entrance') {
        this.form.entranceUrl = ''
        this.$nextTick(() => {
          if (this.$refs.form && this.$refs.form.clearValidate) {
            this.$refs.form.clearValidate()
          }
        })
      }
    }
  },
  created() {
    this.fetchTree()
    this.fetchSpecsMeta()
  },
  mounted() {
    this._onResize = () => {
      this.viewportHeight = window.innerHeight
    }
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    if (this._onResize) window.removeEventListener('resize', this._onResize)
  },
  methods: {
    findById(list, id) {
      if (!Array.isArray(list) || id == null) return null
      const target = String(id)
      return list.find(it => it && String(it.id || it._id) === target) || null
    },
    getSpecMeta(key) {
      const k = key != null ? String(key) : ''
      return k && this.specsMetaMap ? this.specsMetaMap[k] : null
    },
    specLabel(key) {
      const meta = this.getSpecMeta(key)
      return (meta && meta.label) ? meta.label : (key != null ? String(key) : '-')
    },
    getSpecKeyOptionsForItem(item) {
       // 当前表单内：同一个 key 只能选择一次
       // 但编辑时允许当前行保留自己已选的 key
       const currentKey = item && item.key ? String(item.key) : ''
       const selected = new Set(
         (this.form.specItems || [])
           .map(it => (it && it.key ? String(it.key) : ''))
           .filter(Boolean)
       )
       return (this.specKeyOptions || []).filter(opt => {
         const v = opt && opt.value != null ? String(opt.value) : ''
         if (!v) return false
         if (v === currentKey) return true
         return !selected.has(v)
       })
     },

    normalizeTree(list = []) {
      return list.map(item => ({
        ...item,
        id: item.id || item._id,
        parent: item.parent || item.parentId || null,
        children: item.children && item.children.length > 0 ? this.normalizeTree(item.children) : undefined
      }))
    },
    getHasChildrenFlag(item) {
      if (!item) return false
      if (Array.isArray(item.children)) return item.children.length > 0
      const raw = item.hasChildren ?? item._hasChildren ?? item.childrenCount ?? item.childCount ?? item.applianceCount ?? item.productCount
      if (typeof raw === 'boolean') return raw
      if (typeof raw === 'number') return raw > 0
      return item.level !== 'product'
    },
    clearImageUrl(e) {
      e.stopPropagation()
      this.form.picture = ''
      this.imageUrl = ''
    },
    // 获取规格数据
    async fetchSpecsMeta() {
      this.specsMetaLoading = true
      try {
        const { data } = await getSpecifications()

        // 兼容多种返回结构：数组 / 对象
        const list = Array.isArray(data) ? data : (Array.isArray(data?.list) ? data.list : [])
        const map = (data && !Array.isArray(data) && typeof data === 'object' && !Array.isArray(data.list)) ? (data.map || data.specsMetaMap || null) : null

        if (map && typeof map === 'object') {
          this.specsMetaMap = map
          this.specKeyOptions = Object.keys(map).map(k => ({
            label: map[k]?.label || k,
            value: k,
            type: map[k]?.type
          }))
        } else {
          // list item: { key/name/code, label/title, type }
          this.specKeyOptions = (list || []).map(it => ({
            label: it.label || it.title || it.name || it.key || it.code,
            value: it.key || it.code || it.name,
            type: it.type
          })).filter(it => it.value)
          this.specsMetaMap = this.specKeyOptions.reduce((acc, it) => {
            acc[it.value] = { label: it.label, type: it.type }
            return acc
          }, {})
        }
      } catch (e) {
        this.$message && this.$message.warning('获取规格配置失败，将使用手动类型选择')
        this.specKeyOptions = []
        this.specsMetaMap = {}
      } finally {
        this.specsMetaLoading = false
      }
    },
    beforeUpload(file) {
      console.log(file,);
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/gif' || file.type === 'image/bmp' || file.type === 'image/svg+xml'
      if (!isJPG) {
        this.$message.error('上传头像图片只能是 JPG/PNG/GIF/BMP 格式!')
        return false
      }
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isLt2M) {
        this.$message.error('上传头像图片大小不能超过 2MB!')
      }
      return isLt2M
    },
    handleAvatarSuccess(res) {
      this.imageUrl = res.data.imageUrl
      this.form.picture = this.imageUrl
    },
    handleIconSuccess(res) {
      const url = res && res.data && res.data.imageUrl ? res.data.imageUrl : ''
      this.iconUrl = url
      this.form.icon = url
    },

    clearIconUrl(e) {
      if (e && e.stopPropagation) e.stopPropagation()
      this.form.icon = ''
      this.iconUrl = ''
    },
async fetchTree() {
  this.loading = true
  const keepRoomId = this.selectedRoomId
  const keepApplianceId = this.selectedApplianceId
  const keepProductPage = this.productPagination && this.productPagination.page ? this.productPagination.page : 1
  const keepProductPageSize = this.productPagination && this.productPagination.pageSize ? this.productPagination.pageSize : 10

  // 先清空联动面板，避免旧数据闪烁
  this.applianceList = []
  this.productList = []
  this.selectedRoomRow = null
  this.selectedApplianceRow = null
  this.selectedRoomId = null
  this.selectedApplianceId = null
  this.productPagination = { ...this.productPagination, page: 1, total: 0 }

  try {
    const params = {}
    if (this.filterStatus && this.filterStatus !== 'all') params.status = this.filterStatus
    const { data } = await getApplianceRooms(params)
    const normalized = this.normalizeTree(Array.isArray(data) ? data : (data && Array.isArray(data.list) ? data.list : []))
    // 一级列表：不预加载二级
    this.treeData = (normalized || []).map(n => {
      const level = n.level || 'room'
      const hasChildren = this.getHasChildrenFlag(n)
      return {
        ...n,
        level,
        _depth: 0,
        children: undefined,
        hasChildren,
        _hasChildren: hasChildren
      }
    })

    // 尝试恢复刷新前的选中状态
    if (keepRoomId != null) {
      const room = this.findById(this.treeData, keepRoomId)
      if (room) {
        await this.handleRoomRowClick(room)
        if (keepApplianceId != null) {
          const appliance = this.findById(this.applianceList, keepApplianceId)
          if (appliance) {
            await this.handleApplianceRowClick(appliance, { keepPage: keepProductPage, keepPageSize: keepProductPageSize })
          }
        }
      }
    }
  } catch (e) {
    this.$message.error('获取字典数据失败')
  } finally {
    this.loading = false
  }
},

// 统一加载某行的子节点；如果是第三级（product）则支持分页
async loadChildren(row, page = 1, pageSize = 10) {
  if (!row) return
  if (row.level === 'product') return
  // 已加载且不是强制翻页时，直接返回
  if (row._loadedChildren && !row._forceReload) return
  this.$set(row, '_loadingChildren', true)
  try {
    const params = {}
    if (this.filterStatus && this.filterStatus !== 'all') params.status = this.filterStatus

    let resp
    let childLevel = ''
    let needPaging = false

    if (row.level === 'room') {
      params.roomId = row.id
      resp = await getApplianceBundle(params)
      childLevel = 'appliance'
    } else if (row.level === 'appliance') {
      params.applianceId = row.id
      params.page = page
      // 后端分页字段为 limit
      params.limit = pageSize
      resp = await getApplianceProducts(params)
      childLevel = 'product'
      needPaging = true
    } else {
      return
    }

    const data = resp && resp.data
    let list = []
    let total = 0
    if (Array.isArray(data)) {
      list = this.normalizeTree(data)
      total = list.length
    } else if (data && Array.isArray(data.list)) {
      list = this.normalizeTree(data.list)
      total = (data.pagination && data.pagination.total) || data.total || list.length
    } else if (data && Array.isArray(data.appliances)) {
      list = this.normalizeTree(data.appliances)
      total = list.length
    } else if (data && Array.isArray(data.products)) {
      list = this.normalizeTree(data.products)
      total = data.total || list.length
    }

    const nextDepth = (row && typeof row._depth === 'number') ? row._depth + 1 : 1
    list = (list || []).map(n => ({
      ...n,
      level: n.level || childLevel,
      _depth: nextDepth,
      children: undefined,
      hasChildren: this.getHasChildrenFlag(n),
      _hasChildren: this.getHasChildrenFlag(n)
    }))

    this.$set(row, 'children', list)
    if (needPaging) {
      const serverPage = data && data.pagination && data.pagination.page ? Number(data.pagination.page) : Number(page)
      const serverLimit = data && data.pagination && data.pagination.limit ? Number(data.pagination.limit) : Number(pageSize)
      this.$set(row, '_pagination', {
        page: Number(serverPage) || 1,
        pageSize: Number(serverLimit) || 10,
        total: Number(total) || 0,
        pageSizes: [10, 20, 50, 100]
      })
    } else {
      this.$delete(row, '_pagination')
    }
    this.$set(row, '_loadedChildren', true)
  } catch (e) {
    this.$message.error('加载子节点失败')
  } finally {
    this.$set(row, '_loadingChildren', false)
    this.$delete(row, '_forceReload')
  }
},

// 父表格展开变化：按需加载直接子节点（第二级）
handleExpandChange(row) {
  if (!row) return
  if (row.level === 'product') return
  // 当展开时加载
  this.loadChildren(row)
},

// 嵌套表格分页变化：加载父行的子节点（第三级）
handleChildPageChange(parentRow, payload) {
  if (!parentRow) return
  const page = payload && payload.page ? payload.page : (parentRow._pagination && parentRow._pagination.page) || 1
  const pageSize = payload && payload.pageSize ? payload.pageSize : (parentRow._pagination && parentRow._pagination.pageSize) || 10
  // 强制重新加载指定页
  this.$set(parentRow, '_forceReload', true)
  this.loadChildren(parentRow, page, pageSize)
},

    roomRowClassName({ row }) {
      if (!row) return ''
      const id = row.id || row._id
      return this.selectedRoomId != null && String(id) === String(this.selectedRoomId) ? 'dict-row--selected' : ''
    },
    applianceRowClassName({ row }) {
      if (!row) return ''
      const id = row.id || row._id
      return this.selectedApplianceId != null && String(id) === String(this.selectedApplianceId) ? 'dict-row--selected' : ''
    },

    async handleRoomRowClick(row) {
      if (!row) return
      this.selectedRoomRow = row
      this.selectedRoomId = row.id
      this.selectedApplianceRow = null
      this.selectedApplianceId = null
      this.productList = []
      this.productPagination = { ...this.productPagination, page: 1, total: 0 }

      await this.loadChildren(row)
      this.applianceList = Array.isArray(row.children) ? row.children : []
    },

    async handleApplianceRowClick(row, options = {}) {
      if (!row) return
      this.selectedApplianceRow = row
      this.selectedApplianceId = row.id

      const keepPage = options.keepPage
      const keepPageSize = options.keepPageSize
      const nextPage = Number.isFinite(Number(keepPage)) ? Number(keepPage) : 1
      const nextPageSize = Number.isFinite(Number(keepPageSize)) ? Number(keepPageSize) : (this.productPagination.pageSize || 10)

      this.$set(row, '_forceReload', true)
      await this.loadChildren(row, nextPage, nextPageSize)
      this.productList = Array.isArray(row.children) ? row.children : []
      this.productPagination = row._pagination ? { ...row._pagination } : { ...this.productPagination, page: nextPage, pageSize: nextPageSize }
    },

    async handleProductPageChange(payload) {
      if (!this.selectedApplianceRow) return
      const page = payload && payload.page ? payload.page : (this.productPagination.page || 1)
      const pageSize = payload && payload.pageSize ? payload.pageSize : (this.productPagination.pageSize || 10)

      this.$set(this.selectedApplianceRow, '_forceReload', true)
      await this.loadChildren(this.selectedApplianceRow, page, pageSize)
      this.productList = Array.isArray(this.selectedApplianceRow.children) ? this.selectedApplianceRow.children : []
      this.productPagination = this.selectedApplianceRow._pagination ? { ...this.selectedApplianceRow._pagination } : { ...this.productPagination, page, pageSize }
    },
    rowClassName({ row }) {
      const d = row && typeof row._depth === 'number' ? row._depth : null
      return d != null ? `dict-depth-${d}` : ''
    },
    resolvePictureUrl(picture) {
      if (!picture) return ''
      let raw = picture
      if (Array.isArray(raw)) raw = raw[0]
      if (raw && typeof raw === 'object') {
        raw = raw.url || raw.src || raw.path || raw.imageUrl || raw.image || raw.cover || ''
      }
      const s = raw != null ? String(raw) : ''
      if (!s) return ''
      if (/^https?:\/\//i.test(s)) return s
      if (s.startsWith('//')) return `https:${s}`
      // 相对路径：优先预览域名，其次 baseUrl
      const base = this.basePreviewUrl || this.baseUrl || ''
      if (!base) return s
      if (s.startsWith('/')) return `${base}${s}`
      return `${base}/${s}`
    },
    picturePreviewList(picture) {
      const url = this.resolvePictureUrl(picture)
      return url ? [url] : []
    },
    nextLevel(current) {
      if (current === 'room') return 'appliance'
      if (current === 'appliance') return 'product'
      return 'product'
    },
    openAddDialog(level, parent) {
      this.dialogTitle = '新增节点'
      const parentId = parent ? (parent.id || parent._id || parent.data?.id || parent.data?._id || parent.parent || parent.parentId) : null
      const newLevel = parent ? this.nextLevel(parent.level) : level
      this.form = {
        id: null,
        parent: parentId,
        name: '',
        level: newLevel,
        order: 0,
        status: 'enabled',
        score: 0,
        picture: '',
        icon: '',
        bg: '',
        description: '',
        platform: '',
        entranceUrl: '',
        specs: {},
        specItems: [],
        specFeatures: []
      }

      // 非产品层级：清空产品专属字段（避免切换层级导致的脏数据）
      if (newLevel !== 'product') {
        this.form.score = 0
        this.form.platform = ''
        this.form.specs = {}
        this.form.specItems = []
        this.form.specFeatures = []
      }

      // 清理 icon 预览状态
      this.iconUrl = ''
      this.dialogVisible = true
    },
    openEditDialog(node) {
      const data = node && node.data ? node.data : node
      this.dialogTitle = '编辑节点'
      console.log('openEditDialog data=', data);
      // 仅产品层级才需要解析/展示 specs 编辑器
      const { items, features } = data.level === 'product' ? this.specsToEditor(data.specs || {}) : { items: [], features: [] }

      this.form = {
        id: data.id || data._id,
        parent: data.parent || data.parentId || null,
        name: data.name,
        level: data.level,
        order: data.order,
        status: data.status,
        score: data.score,
        picture: data.picture,
        icon: data.icon,
        bg: data.bg || '',
        platform: data.platform,
        description: data.description,
        specs: data.specs || {},
        specItems: items,
        specFeatures: features,
        entranceUrl: data.entranceUrl || data.entrance_url || ''
      }
      // 回填 icon 预览
      this.iconUrl = this.form.icon || ''
      this.dialogVisible = true
    },
    onSpecKeyChange(item) {
      // 选择规格字段后：优先使用接口返回的 type 回显；并清空旧值
      const meta = item && item.key ? this.specsMetaMap[item.key] : null
      if (meta && meta.type) {
        item.type = meta.type
      } else {
        // 未提供 type，保留用户自选（如果之前没选过则清空）
        item.type = ''
      }
      item.value = ''
    },
     genSpecKey() {
       return `spec_${Date.now()}_${Math.random().toString(16).slice(2)}`
     },
     addSpecItem(preset = {}) {
       if (!this.form.specItems) this.$set(this.form, 'specItems', [])
       this.form.specItems.push({
         _key: this.genSpecKey(),
         key: preset.key || '',
         // type：如果 preset 没带（老数据/后端 specs 只有 value），则留空等待用户选择或接口 meta 回显
         type: preset.type || '',
         value: preset.value !== undefined ? preset.value : ''
       })

      // 如果 preset.key 命中 meta，自动回显 type
      const last = this.form.specItems[this.form.specItems.length - 1]
      if (last && last.key && !last.type) {
        const meta = this.specsMetaMap[last.key]
        if (meta && meta.type) last.type = meta.type
      }
     },

     specsToEditor(specs) {
       const items = []
       const features = Array.isArray(specs?.features) ? specs.features : []
       if (specs && typeof specs === 'object') {
         Object.keys(specs).forEach(k => {
           if (k === 'features') return
           const v = specs[k]
           // 优先后端 specsMeta 的类型，其次根据值的类型推断
           const metaType = this.specsMetaMap && this.specsMetaMap[k] ? this.specsMetaMap[k].type : undefined
           const t = typeof v
           const inferred = t === 'number' ? 'number' : t === 'boolean' ? 'boolean' : 'string'
           const type = metaType || inferred
           items.push({ _key: this.genSpecKey(), key: k, type, value: v })
         })
       }
       return { items, features }
     },
    removeSpecItem(index) {
      if (!this.form.specItems) return
      this.form.specItems.splice(index, 1)
    },
    editorToSpecs() {
      const specs = {}
      const used = new Set()
      ;(this.form.specItems || []).forEach(it => {
        const key = (it.key || '').trim()
        if (!key) return
        if (used.has(key)) return
        used.add(key)

        let value = it.value
        if (it.type === 'number') {
          const n = Number(value)
          value = Number.isFinite(n) ? n : value
        } else if (it.type === 'boolean') {
          value = value === true || value === 'true'
        } else {
          value = value == null ? '' : String(value)
        }

        specs[key] = value
      })

      if (Array.isArray(this.form.specFeatures) && this.form.specFeatures.length > 0) {
        specs.features = this.form.specFeatures.filter(Boolean).map(v => String(v))
      }

      return specs
    },

    validateSpecs() {
      const items = this.form.specItems || []
      const keys = items.map(i => (i.key || '').trim()).filter(Boolean)
      const unique = new Set(keys)
      if (keys.length !== unique.size) {
        this.$message.warning('规格项字段名不能重复')
        return false
      }
      // 如果填写了 value 但 key 为空，也不允许
      const invalid = items.some(i => !String(i.key || '').trim() && (i.value !== '' && i.value !== null && i.value !== undefined))
      if (invalid) {
        this.$message.warning('请为已填写的规格值补充字段名')
        return false
      }
      return true
    },

    async submitForm() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        // 仅产品层级需要规格校验
        if (this.isProductLevel && !this.validateSpecs()) return

        const payload = { ...this.form }

        if (this.isProductLevel) {
          payload.specs = this.editorToSpecs()
        } else {
          // 非产品层级：不提交产品专属字段，避免后端结构混乱
          delete payload.specs
          delete payload.score
          delete payload.platform
        }

        // 仅入口层级提交入口地址，其他层级不提交该字段
        if (this.form.level === 'entrance') {
          payload.entranceUrl = this.form.entranceUrl
        } else {
          delete payload.entranceUrl
        }

        // UI 辅助字段一律不提交
        delete payload.specItems
        delete payload.specFeatures

        try {
          if (this.form.id) {
            await updateDictionary(this.form.id, payload)
            this.$message.success('更新成功')
          } else {
            await createDictionary(payload)
            this.$message.success('新增成功')
          }
          this.dialogVisible = false
          this.fetchTree()
        } catch (e) {
          this.$message.error('操作失败')
        }
      })
    },
    async handleDelete(node) {
      this.$confirm('确定删除该节点及其子节点吗？', '提示', {
        type: 'warning'
      }).then(async () => {
        await deleteDictionary(node.id)
        this.$message.success('删除成功')
        this.fetchTree()
      }).catch(() => {})
    },
    getFileNameFromHeaders(headers = {}, fallback = 'dictionary.xlsx') {
      try {
        const key = Object.keys(headers).find(k => k.toLowerCase() === 'content-disposition')
        const cd = key ? headers[key] : ''
        if (!cd) return fallback
        // 支持 filename*=UTF-8''xxx.xlsx
        const fnStar = /filename\*=UTF-8''([^;]+)/i.exec(cd)
        if (fnStar && fnStar[1]) return decodeURIComponent(fnStar[1])
        const fn = /filename=([^;]+)/i.exec(cd)
        if (fn && fn[1]) return fn[1].replace(/(^")|("$)/g, '')
        return fallback
      } catch (e) {
        return fallback
      }
    },

    saveBlobToFile(blob, filename) {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    },

    async handleExportExcel() {
      try {
        const params = {}
        if (this.filterStatus && this.filterStatus !== 'all') params.status = this.filterStatus
        const resp = await exportDictionaryTreeExcel(params)
        // request.js 对 blob 会返回 axios 原始 response
        const blob = resp && resp.data
        const headers = (resp && resp.headers) || {}
        const filename = this.getFileNameFromHeaders(headers, 'dictionary.xlsx')
        this.saveBlobToFile(blob, filename)
      } catch (e) {
        this.$message.error('导出失败')
      }
    },

    async handleDownloadTemplate() {
      try {
        const resp = await exportDictionaryTreeExcelTemplate()
        const blob = resp && resp.data
        const headers = (resp && resp.headers) || {}
        const filename = this.getFileNameFromHeaders(headers, 'dictionary-template.xlsx')
        this.saveBlobToFile(blob, filename)
      } catch (e) {
        this.$message.error('下载模板失败')
      }
    },

    async handleImportChange(file) {
      // ElementUI: file 是 {name, raw, ...}
      const rawFile = file && (file.raw || file)
      if (!rawFile) return

      const name = rawFile.name || ''
      const ok = /\.(xlsx|xls)$/i.test(name)
      if (!ok) {
        this.$message.warning('仅支持 .xlsx / .xls 文件')
        if (this.$refs.excelUpload && this.$refs.excelUpload.clearFiles) this.$refs.excelUpload.clearFiles()
        return
      }

      try {
        await this.$confirm(`导入操作将覆盖原有数据，确定继续导入文件：${name} 吗？`, '导入确认', { type: 'warning' })
      } catch (e) {
        if (this.$refs.excelUpload && this.$refs.excelUpload.clearFiles) this.$refs.excelUpload.clearFiles()
        return
      }

      this.importing = true
      try {
        await importDictionaryTreeExcel(rawFile)
        this.$message.success('导入成功')
        await this.fetchTree()
      } catch (e) {
        this.$message.error('导入失败')
      } finally {
        this.importing = false
        if (this.$refs.excelUpload && this.$refs.excelUpload.clearFiles) this.$refs.excelUpload.clearFiles()
      }
    },
  }
}
</script>

<style scoped>
.dictionary-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-x: hidden;
}

.content-grid {
  display: flex;
  gap: 16px;
  flex-wrap: nowrap;
  align-items: stretch;
  min-width: 0;
  width: 100%;
}

.pane-left {
  flex: 0 0 480px;
  max-width: 480px;
  padding: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
}

.pane-right {
  flex: 1 1 0;
  max-width: none;
  padding: 12px;
  background: #fff;
  min-width: 0;
  overflow: hidden;
}

/* 表格内容如果超宽，让滚动发生在表格内部，而不是页面 */
.dictionary-page ::v-deep .dict-product-table .el-table__body-wrapper {
  overflow-x: auto;
}

.pane-block__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 8px 4px 12px;
}

.pane-block__title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.pane-block__meta {
  font-size: 12px;
  color: #909399;
}

.pane-block__header--right {
  padding-top: 4px;
}

.pane-block__actions {
  flex: 0 0 auto;
}

.dictionary-page ::v-deep tr.dict-row--selected > td {
  background: #f5f7fa !important;
}

/* 修复 CommonTable 将 cell 设为 flex 后导致的对齐失效 */
.dictionary-page ::v-deep td.is-left .cell {
  justify-content: flex-start;
}
.dictionary-page ::v-deep td.is-center .cell {
  justify-content: center;
}
.dictionary-page ::v-deep td.is-right .cell {
  justify-content: flex-end;
}

/* 让展开内容看起来像同一张表（去掉 expanded cell 的内边距/底色/间隔） */
.dictionary-page ::v-deep .el-table__expanded-cell {
  padding: 0 !important;
  background: #fff !important;
  border-bottom: none !important;
}
.dictionary-page ::v-deep .el-table__expanded-cell > .cell {
  padding: 0 !important;
}

/* 内嵌表去掉顶部间隔线，视觉更连续 */
.dictionary-page ::v-deep .el-table__expanded-cell .el-table {
  margin: 0;
}
.dictionary-page ::v-deep .el-table__expanded-cell .el-table--border,
.dictionary-page ::v-deep .el-table__expanded-cell .el-table--group {
  border-top: none;
}

/* 二级箭头缩进（与一级错开） */
.dictionary-page ::v-deep tr.dict-depth-1 td.el-table__expand-column .cell {
  padding-left: 18px !important;
}

/* 嵌套表去掉外边框，避免重叠（保留表格内部网格线视觉） */
.dictionary-page ::v-deep .dict-nested-table .el-table--border,
.dictionary-page ::v-deep .dict-leaf-table .el-table--border {
  border-left: none;
  border-right: none;
  border-bottom: 1px solid #ebeef5;
}

/* 操作列：按钮不换行，右对齐，避免“样式错乱” */
.dictionary-page ::v-deep .el-table__body td.el-table__cell:last-child .cell {
  flex-wrap: nowrap;
  justify-content: flex-end;
}
.dictionary-page ::v-deep .el-table__body td.el-table__cell:last-child .cell .el-button + .el-button {
  margin-left: 12px;
}

/* 固定列阴影/边框在嵌套表里容易错位，直接禁用 */
.dictionary-page ::v-deep .dict-nested-table .el-table__fixed-right,
.dictionary-page ::v-deep .dict-leaf-table .el-table__fixed-right {
  box-shadow: none !important;
}

/* 产品分页更紧凑一点 */
.dictionary-page ::v-deep .dict-leaf-table .common-table__pagination {
  margin-top: 8px;
  padding: 8px 12px 12px;
  border-top: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
}
.dictionary-page ::v-deep .dict-leaf-table .el-pagination {
  padding: 0;
}

/* 图片加载失败时显示短占位，避免“破图+文字”撑高行 */
.dict-img__error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  color: #c0c4cc;
  font-size: 12px;
  background: #f5f7fa;
  border-radius: 4px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
}

.table-card {
  padding: 12px;
  background: #fff;
}

.dialog-footer {
  text-align: right;
}
.el-icon-error {
  position: absolute;
  right: 0;
  color: red;
  top: 0;
}
.upload-demo {
  width: 178px;
  position: relative;
  height: 178px;
  border: 1px dashed var(--primary-500);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.upload-demo /deep/ .el-upload {
  position: absolute;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}
.avatar {
  width: 178px;
  height: 178px;
}
.icon-upload {
  width: 100px;
  position: relative;
  height: 100px;
  border: 1px dashed var(--primary-500);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.icon-upload /deep/ .el-upload {
  position: absolute;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}
.icon-preview {
  width: 80px;
  height: 80px;
  object-fit: cover;
}
.is-disabled {
  border: none !important;
  box-shadow: none !important;
}
.spec-editor {
  width: 100%;
}
.spec-editor__toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}
.spec-editor__empty {
  color: #909399;
  font-size: 12px;
  padding: 6px 0;
}
.spec-editor__row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.spec-editor__key {
  flex: 1.2;
}
.spec-editor__type {
  width: 90px;
}
.spec-editor__value {
  flex: 1.4;
}
.spec-editor__remove {
  color: #f56c6c;
}
.spec-editor__features {
  margin-top: 12px;
}
.spec-editor__features-title {
  color: #606266;
  font-size: 12px;
  margin-bottom: 6px;
}
.spec-editor__features-input {
  width: 100%;
}
.excel-uploader {
  display: inline-block;
  margin-left: 8px;
  margin-right: 8px;
}
</style>
